/**
 * One named blank inside a 203 section box.
 *
 * A seat the Incident Roster fills prints read-only with the info affordance
 * that says so; every other blank is typed and committed on its own, so one
 * deliberate save is one version rather than one per keystroke.
 */

import type React from 'react';

import { BoxInfo } from '../icsBoxPrimitives';
import { SaveableField } from '../SaveableField';
import { ICS203_ROSTER_FIELD_TOOLTIP } from '../ics203BoxLabels';
import {
  getRosterAssigneesDisplay,
  ROSTER_READONLY_SEATS,
} from './ics203Roster';
import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

export type Ics203PositionFieldProps = {
  label: string;
  positionKey: string;
  rosterData: RosterEntry[] | undefined;
  /** Typed names, keyed by position key. */
  assignments: Map<string, string>;
  onSave: (positionKey: string, value: string) => void;
  readOnly?: boolean;
};

/**
 * Turns a position key into something safe to hang a test id and an input id
 * off.
 */
export const positionSlug = (positionKey: string): string =>
  positionKey.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();

export const Ics203PositionField: React.FC<Ics203PositionFieldProps> = ({
  label,
  positionKey,
  rosterData,
  assignments,
  onSave,
  readOnly = false,
}) => {
  const slug = positionSlug(positionKey);

  if (ROSTER_READONLY_SEATS.has(positionKey)) {
    const { display, isAssigned } = getRosterAssigneesDisplay(
      rosterData,
      positionKey
    );
    return (
      <div
        className="tw:flex tw:min-w-0 tw:items-center tw:gap-2"
        data-testid={`ics203-position-${slug}`}
      >
        <span className="tw:shrink-0 tw:text-sm tw:text-muted-foreground">
          {label}:
        </span>
        <span
          className={
            isAssigned
              ? 'tw:min-w-0 tw:break-words tw:text-sm'
              : 'tw:min-w-0 tw:break-words tw:text-sm tw:text-muted-foreground'
          }
        >
          {display}
        </span>
        {/* The tooltip text doubles as the accessible name, which is how the
            end-to-end suite finds these markers. */}
        <BoxInfo label={ICS203_ROSTER_FIELD_TOOLTIP}>
          {ICS203_ROSTER_FIELD_TOOLTIP}
        </BoxInfo>
      </div>
    );
  }

  return (
    <div className="tw:min-w-0" data-testid={`ics203-position-${slug}`}>
      <SaveableField
        id={`ics203-${slug}`}
        label={label}
        value={assignments.get(positionKey) ?? ''}
        placeholder="Enter name..."
        disabled={readOnly}
        onSave={(value) => onSave(positionKey, value)}
      />
    </div>
  );
};

export default Ics203PositionField;
